// src/pages/CCIRAnual.js

import React, { useEffect, useMemo, useState } from "react";

import "./CadastroImoveis.css";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import CloseIcon from "@mui/icons-material/Close";

import ThemeProvider from "../theme/TemaMuiProvider";

import { FaCopy, FaEdit, FaEye, FaFilter, FaPaperclip, FaSave, FaTimes, FaTrash } from "react-icons/fa";

import { jwtDecode } from "jwt-decode";

import muiTheme from "../theme/muiTheme";

import { copiarTexto } from "../utils/clipboard";
import { limparCpfCnpj, mascararCpfCnpj, tipoDocumento } from "../utils/documento";

const API = "/api/ccir-anual";

function authHeaders() {
  const token = localStorage.getItem("token");

  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function jsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function onlyDate(value) {
  return value ? String(value).slice(0, 10) : "";
}

function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")

    .replace(/[\u0300-\u036f]/g, "")

    .toLowerCase();
}

function digits(value) {
  return String(value ?? "").replace(/\D/g, "");
}

// CPF ou CNPJ (numérico ou alfanumérico).
function formatDocument(value) {
  return mascararCpfCnpj(value);
}

function personTypeFromDocument(value) {
  const tipo = tipoDocumento(value);

  if (tipo === "CPF") return "PF";

  if (tipo === "CNPJ") return "PJ";

  return "";
}

function toBrDecimal(value, scale = 4) {
  if (value === null || value === undefined || value === "") return "";

  const number = typeof value === "number" ? value : Number(String(value).replace(/\./g, "").replace(",", "."));

  if (!Number.isFinite(number)) return "";

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: scale,

    maximumFractionDigits: scale,
  });
}

function decimalTyping(value, scale = 4) {
  const clean = String(value ?? "").replace(/[^\d,]/g, "");

  const [integer = "", ...rest] = clean.split(",");

  const intClean = integer.replace(/^0+(?=\d)/, "");

  const dec = rest.join("").slice(0, scale);

  const intFormatted = intClean.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return clean.includes(",") ? `${intFormatted},${dec}` : intFormatted;
}

function toBrMoney(value) {
  if (value === null || value === undefined || value === "") return "";

  const number = typeof value === "number" ? value : Number(String(value).replace(/\./g, "").replace(",", "."));

  if (!Number.isFinite(number)) return "";

  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,

    maximumFractionDigits: 2,
  });
}

function moneyTyping(value) {
  const raw = digits(value);

  if (!raw) return "";

  return (Number(raw) / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,

    maximumFractionDigits: 2,
  });
}

function getId(item) {
  return item?.id ?? item?.ccirAnualId ?? null;
}

const emptyHolder = () => ({
  documento: "",

  nome: "",

  tipoPessoa: "",

  condicao: "",

  detencao: "",
});

const initialForm = (year = "") => ({
  ano: String(year || ""),

  ccirId: null,

  codigoCcir: "",

  codigoAnual: "",

  codigoImovelRural: "",

  codigoCar: "",

  registroCar: "",

  nomeImovelRural: "",

  apelido: "",

  codigoMatricula: "",

  municipioSedeImovelRural: "",

  uf: "",

  areaTotalHa: "",

  moduloRuralHa: "",

  numeroModulosRurais: "",

  moduloFiscalHa: "",

  numeroModulosFiscais: "",

  fracaoMinimaParcelamentoHa: "",

  ufMunicipioCartorio: "",

  dataRegistro: "",

  cnsOuOficio: "",

  matriculaTranscricao: "",

  registro: "",

  livroFicha: "",

  areaRegistrada: "",

  posseJustoTitulo: "",

  posseSimplesOcupacao: "",

  areaMedida: "",

  declaranteDocumento: "",

  declaranteNome: "",

  declaranteTipoPessoa: "",

  totalPessoasRelacionadas: "",

  titulares: [emptyHolder()],

  dataLancamento: "",

  numeroCcirDocumento: "",

  dataGeracaoCcir: "",

  debitosAnteriores: "",

  taxasServicosCadastrais: "",

  valorCobrado: "",

  observacao: "",
});

const requiredFields = [
  "ano",

  "ccirId",

  "moduloRuralHa",

  "numeroModulosRurais",

  "moduloFiscalHa",

  "numeroModulosFiscais",

  "fracaoMinimaParcelamentoHa",

  "ufMunicipioCartorio",

  "dataRegistro",

  "cnsOuOficio",

  "matriculaTranscricao",

  "registro",

  "livroFicha",

  "areaRegistrada",

  "posseJustoTitulo",

  "posseSimplesOcupacao",

  "areaMedida",

  "declaranteDocumento",

  "declaranteNome",

  "declaranteTipoPessoa",

  "totalPessoasRelacionadas",

  "dataLancamento",

  "numeroCcirDocumento",

  "dataGeracaoCcir",

  "debitosAnteriores",

  "taxasServicosCadastrais",

  "valorCobrado",
];

const decimal4Fields = [
  "moduloRuralHa",

  "numeroModulosRurais",

  "moduloFiscalHa",

  "numeroModulosFiscais",

  "areaRegistrada",

  "posseJustoTitulo",

  "posseSimplesOcupacao",

  "areaMedida",
];

const decimal2Fields = ["fracaoMinimaParcelamentoHa"];

const moneyFields = ["debitosAnteriores", "taxasServicosCadastrais", "valorCobrado"];

export default function CCIRAnual() {
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState("");

  const [availableYears, setAvailableYears] = useState([]);

  const [records, setRecords] = useState([]);

  const [filtered, setFiltered] = useState([]);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);

  const [gridMinimized, setGridMinimized] = useState(() => localStorage.getItem("ccirAnualGridMinimizado") === "1");

  const [form, setForm] = useState(initialForm(currentYear));

  const [showPending, setShowPending] = useState(false);

  const [viewingId, setViewingId] = useState(null);

  const [editingId, setEditingId] = useState(null);

  const [viewMode, setViewMode] = useState(false);

  const [ccirs, setCcirs] = useState([]);

  const [ccirDialog, setCcirDialog] = useState(false);

  const [ccirSearch, setCcirSearch] = useState("");

  const [ccirPage, setCcirPage] = useState(0);

  const [ccirRows, setCcirRows] = useState(10);

  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [files, setFiles] = useState([]);

  const [pendingFiles, setPendingFiles] = useState([]);

  const [gedDialog, setGedDialog] = useState(false);

  const [gedRecordId, setGedRecordId] = useState(null);

  const [consultandoDeclarante, setConsultandoDeclarante] = useState(false);
  const [titularConsultando, setTitularConsultando] = useState(null);

  const locked = viewMode && !editingId;

  useEffect(() => {
    localStorage.setItem("ccirAnualGridMinimizado", gridMinimized ? "1" : "0");
  }, [gridMinimized]);

  const token = localStorage.getItem("token");

  let userType = "";

  if (token) {
    try {
      userType = normalizeText(jwtDecode(token)?.tipo);
    } catch {}
  }

  const canView = ["visitante", "usuario padrao", "gestor", "administrador"].includes(userType);

  const canWrite = ["usuario padrao", "gestor", "administrador"].includes(userType);

  const canDeleteRecord = userType === "administrador";

  const canDeleteFile = ["gestor", "administrador"].includes(userType);

  const fieldError = (field) => {
    if (!showPending || !requiredFields.includes(field)) return false;

    if (field === "ccirId") return !form.ccirId;

    const value = form[field];

    return value === null || value === undefined || String(value).trim() === "";
  };

  const holderError = (holder, field) => {
    if (!showPending) return false;

    return String(holder?.[field] ?? "").trim() === "";
  };

  const muiFieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,

      "& fieldset": { borderColor: "var(--ig-b-555555)" },

      "&:hover fieldset": { borderColor: "var(--ig-b-777777)" },

      "&.Mui-focused fieldset": { borderColor: "#2ea8ff", borderWidth: 2 },

      "&.Mui-error fieldset": { borderColor: "#ef4444 !important", borderWidth: 2 },

      "&.Mui-disabled fieldset": { borderColor: "var(--ig-b-555555)" },
    },

    "& .MuiOutlinedInput-input": { color: "var(--ig-t-ffffff)" },

    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "var(--ig-t-ffffff)" },

    "& .MuiInputLabel-root": { color: "var(--ig-t-ffffff)" },

    "& .MuiInputLabel-root.Mui-focused": { color: "#2ea8ff" },

    "& .MuiInputLabel-root.Mui-error": { color: "#ef4444 !important" },

    "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ig-t-ffffff)" },

    "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" },

    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: "var(--ig-x-11r8gad)",
      opacity: 1,
      cursor: "pointer",
    },
  };

  const darkMenu = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827)",

          color: "var(--ig-t-ffffff)",

          "& .MuiMenuItem-root:hover": { backgroundColor: "var(--ig-f-1f2937)" },

          "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "var(--ig-f-1f2937) !important" },
        },
      },
    },
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const start = (page - 1) * rowsPerPage;

  const end = start + rowsPerPage;

  const pageRecords = filtered.slice(start, end);

  function addAvailableYear(value) {
    const year = Number(value);

    if (!Number.isInteger(year)) return;

    setAvailableYears((old) => [...new Set([...old, year])].sort((a, b) => a - b));
  }

  async function getJson(url, options = {}) {
    const response = await fetch(url, {
      ...options,

      headers: { ...authHeaders(), ...(options.headers || {}) },
    });

    const data = await jsonSafe(response);

    if (!response.ok) throw new Error(data?.erro || data?.mensagem || `Erro HTTP ${response.status}`);

    return data;
  }

  async function loadYears() {
    try {
      const data = await getJson(`${API}/listar-anos`);

      setAvailableYears(Array.isArray(data) ? data.map(Number).filter(Number.isInteger) : []);
    } catch (error) {
      console.error("Erro ao carregar anos do CCIR Anual:", error);

      setAvailableYears([]);
    }
  }

  async function loadCcirs(year = form.ano || selectedYear) {
    try {
      const query = year ? `?ano=${encodeURIComponent(year)}` : "";

      const data = await getJson(`${API}/ccirs-disponiveis${query}`);

      setCcirs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar CCIRs disponíveis:", error);

      setCcirs([]);
    }
  }

  async function loadYear(year, keepPage = false) {
    if (!year) return;

    setLoading(true);

    try {
      const data = await getJson(`${API}/${year}`);

      const list = Array.isArray(data) ? data : [];

      setRecords(list);

      if (list.length) addAvailableYear(year);

      applyFilter(search, list);

      if (!keepPage) setPage(1);
    } catch (error) {
      console.error(`Erro ao carregar CCIR Anual ${year}:`, error);

      setRecords([]);

      setFiltered([]);

      if (!keepPage) setPage(1);

      alert(error.message || `Erro ao carregar CCIR ${year}.`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadYears();

    loadCcirs(currentYear);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      setRecords([]);

      setFiltered([]);

      return;
    }

    loadYear(selectedYear);

    loadCcirs(selectedYear);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    const currentId = editingId || viewingId;

    if (!currentId) {
      setFiles([]);

      return;
    }

    loadFiles(currentId, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, viewingId]);

  function applyFilter(text, base = records) {
    const terms = normalizeText(text)
      .split(/[,.;\s]+/)
      .filter(Boolean);

    const result = (base || []).filter((item) => {
      const haystack = normalizeText(Object.values(item || {}).join(" "));

      return terms.every((term) => haystack.includes(term));
    });

    setFiltered(result);
  }

  function handleSearch(event) {
    const value = event.target.value;

    setSearch(value);

    applyFilter(value);

    setPage(1);
  }

  function clearFilter() {
    setSearch("");

    setFiltered(records);

    setPage(1);
  }

  const visibleCcirs = useMemo(() => {
    const term = normalizeText(ccirSearch);

    return (ccirs || []).filter((item) => {
      if (item.possuiRegistroNoAno && Number(item.ccirAnualId) !== Number(editingId || viewingId)) {
        return false;
      }

      if (!term) return true;

      return normalizeText(
        [
          item.codigoCcir,

          item.codigoImovelRural,

          item.codigoCar,

          item.registroCar,

          item.nomeImovelRural,

          item.apelido,

          item.codigoMatricula,

          item.municipioSedeImovelRural,

          item.uf,
        ]
          .filter(Boolean)
          .join(" "),
      ).includes(term);
    });
  }, [ccirs, ccirSearch, editingId, viewingId]);

  const pagedCcirs = visibleCcirs.slice(ccirPage * ccirRows, ccirPage * ccirRows + ccirRows);

  function selectCcir(item) {
    setForm((old) => ({
      ...old,

      ccirId: item.ccirId,

      codigoCcir: item.codigoCcir || "",

      codigoAnual: item.codigoCcir && old.ano ? `${item.codigoCcir}/${old.ano}` : "",

      codigoImovelRural: item.codigoImovelRural || "",

      codigoCar: item.codigoCar || "",

      registroCar: item.registroCar || "",

      nomeImovelRural: item.nomeImovelRural || "",

      apelido: item.apelido || "",

      codigoMatricula: item.codigoMatricula || "",

      municipioSedeImovelRural: item.municipioSedeImovelRural || "",

      uf: item.uf || "",

      areaTotalHa: toBrDecimal(item.areaTotalHa, 4),
    }));

    setCcirDialog(false);

    setCcirSearch("");

    setCcirPage(0);
  }

  async function consultarCnpj(documento) {
    const documentoLimpo = limparCpfCnpj(documento);

    if (documentoLimpo.length !== 14) {
      return null;
    }

    const params = new URLSearchParams({
      tipo: "PJ",
      documento: documentoLimpo,
    });

    const response = await fetch(`/api/proprietarios/consulta-documento?${params.toString()}`, {
      headers: authHeaders(),
    });

    const data = await jsonSafe(response);

    if (!response.ok) {
      throw new Error(data?.erro || data?.mensagem || "CNPJ não localizado.");
    }

    return {
      nome: String(data?.nome || "")
        .trim()
        .toUpperCase(),
    };
  }

  async function handleDeclaranteDocumentoChange(value) {
    const documentoFormatado = formatDocument(value);
    const documentoLimpo = limparCpfCnpj(documentoFormatado);
    const tipoPessoa = personTypeFromDocument(documentoLimpo);

    setForm((old) => ({
      ...old,
      declaranteDocumento: documentoFormatado,
      declaranteTipoPessoa: tipoPessoa,
      declaranteNome: documentoLimpo.length < 14 && old.declaranteTipoPessoa === "PJ" ? "" : old.declaranteNome,
    }));

    if (documentoLimpo.length !== 14) {
      return;
    }

    setConsultandoDeclarante(true);

    try {
      const resultado = await consultarCnpj(documentoLimpo);

      setForm((old) => ({
        ...old,
        declaranteDocumento: formatDocument(documentoLimpo),
        declaranteTipoPessoa: "PJ",
        declaranteNome: resultado?.nome || old.declaranteNome,
      }));
    } catch (error) {
      console.error("Erro ao consultar CNPJ do declarante:", error);

      alert(error?.message || "CNPJ não localizado. Informe o nome manualmente.");
    } finally {
      setConsultandoDeclarante(false);
    }
  }

  async function handleTitularDocumentoChange(index, value) {
    const documentoFormatado = formatDocument(value);
    const documentoLimpo = limparCpfCnpj(documentoFormatado);
    const tipoPessoa = personTypeFromDocument(documentoLimpo);

    setForm((old) => ({
      ...old,
      titulares: old.titulares.map((holder, holderIndex) => {
        if (holderIndex !== index) {
          return holder;
        }

        return {
          ...holder,
          documento: documentoFormatado,
          tipoPessoa,
          nome: documentoLimpo.length < 14 && holder.tipoPessoa === "PJ" ? "" : holder.nome,
        };
      }),
    }));

    if (documentoLimpo.length !== 14) {
      return;
    }

    setTitularConsultando(index);

    try {
      const resultado = await consultarCnpj(documentoLimpo);

      setForm((old) => ({
        ...old,
        titulares: old.titulares.map((holder, holderIndex) => {
          if (holderIndex !== index) {
            return holder;
          }

          return {
            ...holder,
            documento: formatDocument(documentoLimpo),
            tipoPessoa: "PJ",
            nome: resultado?.nome || holder.nome,
          };
        }),
      }));
    } catch (error) {
      console.error(`Erro ao consultar CNPJ do Titular ${index + 1}:`, error);

      alert(error?.message || `CNPJ do Titular ${index + 1} não localizado. Informe o nome manualmente.`);
    } finally {
      setTitularConsultando(null);
    }
  }

  function updateForm(field, value) {
    setForm((old) => {
      const next = { ...old, [field]: value };

      if (field === "ano") {
        next.codigoAnual = next.codigoCcir && value ? `${next.codigoCcir}/${value}` : "";
      }

      return next;
    });
  }

  function updateHolder(index, field, value) {
    setForm((old) => ({
      ...old,

      titulares: old.titulares.map((holder, holderIndex) => {
        if (holderIndex !== index) return holder;

        const next = { ...holder, [field]: value };

        if (field === "documento") next.tipoPessoa = personTypeFromDocument(value);

        return next;
      }),
    }));
  }

  function addHolder() {
    if (locked) return;

    setForm((old) => ({ ...old, titulares: [...old.titulares, emptyHolder()] }));
  }

  function removeHolder(index) {
    if (locked) return;

    setForm((old) => ({
      ...old,

      titulares: old.titulares.length === 1 ? [emptyHolder()] : old.titulares.filter((_, holderIndex) => holderIndex !== index),
    }));
  }

  function clearForm(year = selectedYear || "") {
    setForm(initialForm(year));

    setViewingId(null);

    setEditingId(null);

    setViewMode(false);

    setShowPending(false);

    setFiles([]);

    setPendingFiles([]);

    setGedDialog(false);

    setGedRecordId(null);
  }

  async function viewRecord(item) {
    if (!canView) return alert("Você não tem permissão para visualizar.");

    try {
      const full = await getJson(`${API}/registro/${getId(item)}`);

      setForm({
        ...initialForm(full.ano),

        ...full,

        ano: String(full.ano || ""),

        codigoCcir: full.codigoCcir || "",

        codigoAnual: full.codigoAnual || "",

        areaTotalHa: toBrDecimal(full.areaTotalHa, 4),

        moduloRuralHa: toBrDecimal(full.moduloRuralHa, 4),

        numeroModulosRurais: toBrDecimal(full.numeroModulosRurais, 4),

        moduloFiscalHa: toBrDecimal(full.moduloFiscalHa, 4),

        numeroModulosFiscais: toBrDecimal(full.numeroModulosFiscais, 4),

        fracaoMinimaParcelamentoHa: toBrDecimal(full.fracaoMinimaParcelamentoHa, 2),

        dataRegistro: onlyDate(full.dataRegistro),

        areaRegistrada: toBrDecimal(full.areaRegistrada, 4),

        posseJustoTitulo: toBrDecimal(full.posseJustoTitulo, 4),

        posseSimplesOcupacao: toBrDecimal(full.posseSimplesOcupacao, 4),

        areaMedida: toBrDecimal(full.areaMedida, 4),

        declaranteDocumento: formatDocument(full.declaranteDocumento),

        totalPessoasRelacionadas: String(full.totalPessoasRelacionadas ?? ""),

        titulares: (full.titulares || []).length
          ? full.titulares.map((holder) => ({
              ...holder,

              documento: formatDocument(holder.documento),

              detencao: toBrDecimal(holder.detencao, 2),
            }))
          : [emptyHolder()],

        dataLancamento: onlyDate(full.dataLancamento),

        dataGeracaoCcir: onlyDate(full.dataGeracaoCcir),

        debitosAnteriores: toBrMoney(full.debitosAnteriores),

        taxasServicosCadastrais: toBrMoney(full.taxasServicosCadastrais),

        valorCobrado: toBrMoney(full.valorCobrado),

        observacao: full.observacao || "",
      });
      setSelectedYear(Number(full.ano));

      setViewingId(getId(full));

      setEditingId(null);

      setViewMode(true);

      setShowPending(true);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);

      alert(error.message || "Erro ao consultar o CCIR Anual.");
    }
  }

  function startEditing() {
    if (!canWrite) return alert("Você não tem permissão para editar.");

    setEditingId(viewingId);

    setViewMode(false);

    setShowPending(true);
  }

  function validateForm() {
    const missing = requiredFields.some((field) => fieldError(field));

    if (missing) return "Preencha os campos obrigatórios destacados em vermelho.";

    if (!form.titulares?.length) return "Cadastre ao menos um titular.";

    let total = 0;

    for (let index = 0; index < form.titulares.length; index += 1) {
      const holder = form.titulares[index];

      if (!limparCpfCnpj(holder.documento) || !holder.nome || !holder.tipoPessoa || !holder.condicao || !holder.detencao) {
        return `Preencha todos os campos do Titular ${index + 1}.`;
      }

      const value = Number(String(holder.detencao).replace(/\./g, "").replace(",", "."));

      if (!Number.isFinite(value) || value < 0 || value > 100) {
        return `A detenção do Titular ${index + 1} deve estar entre 0,00 e 100,00.`;
      }

      total += value;
    }

    if (total > 100.0001) return "A soma da detenção dos titulares não pode ultrapassar 100,00%.";

    return null;
  }

  function buildPayload() {
    return {
      ...form,

      ano: Number(form.ano),

      ccirId: Number(form.ccirId),

      declaranteDocumento: limparCpfCnpj(form.declaranteDocumento),

      totalPessoasRelacionadas: Number(form.totalPessoasRelacionadas),

      titulares: form.titulares.map((holder) => ({
        ...holder,

        documento: limparCpfCnpj(holder.documento),
      })),
    };
  }

  async function submit(event) {
    event.preventDefault();

    if (!editingId && !canWrite) return alert("Você não tem permissão para cadastrar.");

    if (editingId && !canWrite) return alert("Você não tem permissão para editar.");

    setShowPending(true);

    const validation = validateForm();

    if (validation) return alert(validation);

    const year = Number(form.ano);

    if (!Number.isInteger(year) || year < 2000 || year > 2100) {
      return alert("Informe um ano válido para o CCIR.");
    }

    try {
      const response = await fetch(editingId ? `${API}/${year}/${editingId}` : `${API}/${year}`, {
        method: editingId ? "PUT" : "POST",

        headers: { "Content-Type": "application/json", ...authHeaders() },

        body: JSON.stringify(buildPayload()),
      });

      const data = await jsonSafe(response);

      if (!response.ok) throw new Error(data?.erro || "Falha ao salvar o CCIR Anual.");

      const savedId = editingId || data?.ccirAnualId || data?.id;

      alert(editingId ? `CCIR ${year} atualizado com sucesso!` : `CCIR ${year} cadastrado com sucesso!`);

      addAvailableYear(year);

      setSelectedYear(year);

      if (pendingFiles.length && savedId) {
        const ok = await uploadFiles(savedId, pendingFiles);

        if (ok) {
          setPendingFiles([]);

          alert("Anexos GED enviados com sucesso!");
        } else {
          alert("O registro foi salvo, mas ocorreu uma falha ao enviar os anexos GED.");
        }
      }

      await loadYear(year, Boolean(editingId));

      await loadCcirs(year);

      if (editingId) {
        const id = editingId;

        setViewingId(id);

        setEditingId(null);

        setViewMode(true);

        await loadFiles(id, false);
      } else {
        clearForm(year);
      }
    } catch (error) {
      console.error("Erro ao salvar CCIR Anual:", error);

      alert(error.message || "Erro ao salvar o CCIR Anual.");
    }
  }

  async function deleteRecord() {
    if (!canDeleteRecord) return alert("Somente administradores podem excluir este registro.");

    if (!viewingId) return alert("Registro inválido.");

    if (!window.confirm(`Deseja excluir este registro de CCIR ${selectedYear}?`)) return;

    try {
      const response = await fetch(`${API}/${selectedYear}/${viewingId}`, {
        method: "DELETE",

        headers: authHeaders(),
      });

      const data = await jsonSafe(response);

      if (!response.ok) throw new Error(data?.erro || "Falha ao excluir o registro.");

      alert("Registro excluído com sucesso!");

      clearForm(selectedYear);

      await loadYear(selectedYear);

      await loadCcirs(selectedYear);
    } catch (error) {
      console.error(error);

      alert(error.message || "Erro ao excluir o registro.");
    }
  }

  async function uploadFiles(id, selectedFiles) {
    const formData = new FormData();

    selectedFiles.forEach((file) => formData.append("files", file));

    try {
      const response = await fetch(`${API}/${id}/ged`, {
        method: "POST",

        headers: authHeaders(),

        body: formData,
      });

      return response.ok;
    } catch (error) {
      console.error("Erro ao enviar GED:", error);

      return false;
    }
  }

  async function loadFiles(id, openDialog = true) {
    if (!id) return;

    if (openDialog) {
      setGedRecordId(id);

      setGedDialog(true);
    }

    try {
      const data = await getJson(`${API}/${id}/ged`);

      setFiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar arquivos GED:", error);

      setFiles([]);
    }
  }

  async function viewFile(recordId, file) {
    const extension = String(file?.nome || "").toLowerCase();

    const viewable = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg", ".txt"].some((suffix) => extension.endsWith(suffix));

    if (!viewable) {
      if (window.confirm("Este tipo de arquivo não possui visualização integrada. Deseja baixar agora?")) {
        downloadFile(recordId, file);
      }

      return;
    }

    try {
      const response = await fetch(`${API}/${recordId}/ged/${file.id}/visualizar`, { headers: authHeaders() });

      if (!response.ok) throw new Error("Falha ao visualizar o arquivo.");

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error(error);

      alert("Erro ao visualizar o arquivo GED.");
    }
  }

  async function downloadFile(recordId, file) {
    try {
      const response = await fetch(`${API}/${recordId}/ged/${file.id}`, { headers: authHeaders() });

      if (!response.ok) throw new Error("Falha ao baixar o arquivo.");

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      const anchor = document.createElement("a");

      anchor.href = url;

      anchor.download = file.nome || "arquivo";

      document.body.appendChild(anchor);

      anchor.click();

      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);

      alert("Erro ao baixar o arquivo GED.");
    }
  }

  async function deleteFile(recordId, fileId) {
    if (!canDeleteFile) return alert("Você não tem permissão para excluir arquivos GED.");

    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const response = await fetch(`${API}/${recordId}/ged/${fileId}`, {
        method: "DELETE",

        headers: authHeaders(),
      });

      const data = await jsonSafe(response);

      if (!response.ok) throw new Error(data?.erro || "Falha ao excluir o arquivo.");

      setFiles((old) => old.filter((file) => Number(file.id) !== Number(fileId)));

      alert("Arquivo GED excluído com sucesso!");

      if (selectedYear) await loadYear(selectedYear, true);
    } catch (error) {
      console.error(error);

      alert(error.message || "Erro ao excluir o arquivo GED.");
    }
  }

  async function copyTable() {
    const text = filtered
      .map((item) => [item.codigoAnual, item.codigoCcir, item.apelido, item.nomeImovelRural, item.numeroCcirDocumento, item.valorCobrado].join("\t"))
      .join("\n");

    const ok = await copiarTexto(text);

    alert(ok ? "Registros copiados!" : "Não foi possível copiar os registros.");
  }

  function exportCsv() {
    const header = [
      "Código Anual",
      "Código CCIR",
      "Código Imóvel Rural",
      "CAR",
      "Apelido",

      "Nome do Imóvel Rural",
      "Município",
      "UF",
      "Módulo Rural (ha)",

      "Nº Módulos Rurais",
      "Módulo Fiscal (ha)",
      "Nº Módulos Fiscais",

      "Fração Mínima Parcelamento",
      "UF/Município Cartório",
      "Data Registro",

      "CNS/Ofício",
      "Matrícula/Transcrição",
      "Registro",
      "Livro/Ficha",

      "Área Registrada",
      "Posse Justo Título",
      "Posse Simples Ocupação",
      "Área Medida",

      "Documento Declarante",
      "Nome Declarante",
      "Tipo Pessoa",
      "Total Pessoas",

      "Data Lançamento",
      "Número CCIR",
      "Data Geração",
      "Débitos Anteriores",

      "Taxas Serviços Cadastrais",
      "Valor Cobrado",
      "Observação",
      "Qtd. Titulares",
      "Qtd. Arquivos",
    ];

    const escape = (value) => {
      const text = String(value ?? "");

      return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };

    const rows = filtered.map((item) => [
      item.codigoAnual,
      item.codigoCcir,
      item.codigoImovelRural,
      item.codigoCar,
      item.apelido,

      item.nomeImovelRural,
      item.municipioSedeImovelRural,
      item.uf,
      item.moduloRuralHa,

      item.numeroModulosRurais,
      item.moduloFiscalHa,
      item.numeroModulosFiscais,

      item.fracaoMinimaParcelamentoHa,
      item.ufMunicipioCartorio,
      onlyDate(item.dataRegistro),

      item.cnsOuOficio,
      item.matriculaTranscricao,
      item.registro,
      item.livroFicha,

      item.areaRegistrada,
      item.posseJustoTitulo,
      item.posseSimplesOcupacao,
      item.areaMedida,

      item.declaranteDocumento,
      item.declaranteNome,
      item.declaranteTipoPessoa,

      item.totalPessoasRelacionadas,
      onlyDate(item.dataLancamento),
      item.numeroCcirDocumento,

      onlyDate(item.dataGeracaoCcir),
      item.debitosAnteriores,
      item.taxasServicosCadastrais,

      item.valorCobrado,
      item.observacao,
      item.qtdTitulares,
      item.qtdArquivos,
    ]);

    const csv = [header, ...rows].map((row) => row.map(escape).join(";")).join("\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });

    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;

    anchor.download = `ccir_${selectedYear}.csv`;

    document.body.appendChild(anchor);

    anchor.click();

    anchor.remove();

    URL.revokeObjectURL(url);
  }

  const hasGed = files.length > 0 || pendingFiles.length > 0 || Number(records.find((item) => getId(item) === (editingId || viewingId))?.qtdArquivos || 0) > 0;

  if (!canView) {
    return (
      <div className="local-container">
        <h2>CCIR ANUAL</h2>

        <p className="aviso-permissao">🔒 Você não possui permissão para visualizar esta tela.</p>
      </div>
    );
  }

  const sectionTitle = (title) => (
    <Box
      sx={{
        width: "100%",
        mt: 2.5,
        mb: 1,
        pb: 1,
        borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
      }}
    >
      <h3
        style={{
          margin: 0,
          color: "var(--ig-t-ffffff)",
          fontSize: "1.05rem",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {title}
      </h3>
    </Box>
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>CCIR ANUAL</h2>

        <FormControl size="small" sx={{ minWidth: 320, maxWidth: 360, mb: 3 }}>
          <InputLabel>Buscar Ano</InputLabel>

          <Select
            value={selectedYear}

            label="Buscar Ano"

            onChange={(event) => {
              const value = event.target.value;

              setSearch("");

              setPage(1);

              if (!value) {
                setSelectedYear("");

                clearForm("");

                return;
              }

              const year = Number(value);

              setSelectedYear(year);

              clearForm(year);
            }}

            sx={{ background: "var(--ig-f-1b2433)", color: "var(--ig-t-ffffff)", "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" } }}

            MenuProps={{ PaperProps: { sx: { bgcolor: "var(--ig-f-1b2433)", color: "var(--ig-t-ffffff)" } } }}
          >
            <MenuItem value="">Selecione...</MenuItem>

            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                CCIR {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedYear && (
          <Box sx={{ display: "flex", gap: 1.5, mb: 2, width: "100%" }}>
            <TextField
              fullWidth

              placeholder={`Filtrar CCIR ${selectedYear}`}

              value={search}

              onChange={handleSearch}

              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <FaFilter />
                  </InputAdornment>
                ),
              }}

              sx={{ "& .MuiOutlinedInput-root": { height: 60, bgcolor: "#0f172a", color: "#fff" } }}
            />

            <Button variant="outlined" onClick={clearFilter} sx={{ minWidth: 130 }}>
              Limpar Filtro
            </Button>

            <Button variant="contained" startIcon={<FaCopy />} onClick={copyTable}>
              Copiar
            </Button>

            <Button variant="contained" onClick={exportCsv}>
              CSV
            </Button>
          </Box>
        )}

        <div className="grid-toggle-area right">
          <button
            type="button"

            className={`btn-grid-toggle small ${gridMinimized ? "outline" : "danger"}`}

            onClick={() => setGridMinimized((old) => !old)}
          >
            {gridMinimized ? "↗ Expandir" : "↘ Minimizar"}
          </button>
        </div>

        {!selectedYear && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">📅 Selecione um ano para visualizar os registros de CCIR.</p>
          </div>
        )}

        {selectedYear && !gridMinimized && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando...</p>
            ) : filtered.length === 0 ? (
              <p>Nenhum registro de CCIR {selectedYear} encontrado.</p>
            ) : (
              <>
                <div className="tabela-scroll">
                  <table className="tabela-iptu-anual">
                    <thead>
                      <tr>
                        <th>Código Anual</th>

                        <th>CCIR</th>

                        <th>Apelido</th>

                        <th>Imóvel Rural</th>

                        <th>Número CCIR</th>

                        <th>Valor Cobrado</th>

                        <th>Titulares</th>

                        <th style={{ width: 260, minWidth: 260 }}>Ações</th>
                      </tr>
                    </thead>

                    <tbody>
                      {pageRecords.map((item) => (
                        <tr key={getId(item)}>
                          <td>{item.codigoAnual}</td>

                          <td>{item.codigoCcir}</td>

                          <td>{item.apelido}</td>

                          <td>{item.nomeImovelRural}</td>

                          <td>{item.numeroCcirDocumento}</td>

                          <td>{toBrMoney(item.valorCobrado)}</td>

                          <td>{item.qtdTitulares || 0}</td>

                          <td
                            className="col-acoes"
                            style={{
                              width: 260,
                              minWidth: 260,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: 8,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <button
                              type="button"

                              className={`btn-ged-lista ${Number(item.qtdArquivos) > 0 ? "btn-ged-ok" : ""}`}

                              onClick={() => loadFiles(getId(item), true)}
                            >
                              GED {Number(item.qtdArquivos) > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                            </button>

                            <button type="button" className="btn-editar btn-visualizar-lista" onClick={() => viewRecord(item)}>
                              <FaEye /> Visualizar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3, color: "var(--ig-t-ffffff)", mt: 1 }}>
                  <span>CCIR {selectedYear} por página:</span>

                  <Select
                    size="small"

                    value={rowsPerPage}

                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(1);
                    }}

                    sx={{ color: "var(--ig-t-ffffff)", ".MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" } }}
                  >
                    {[5, 10, 25].map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>

                  <span>{filtered.length ? `${start + 1}–${Math.min(end, filtered.length)} de ${filtered.length}` : "0–0 de 0"}</span>

                  <Button disabled={page <= 1} onClick={() => setPage((old) => Math.max(1, old - 1))}>
                    ‹
                  </Button>

                  <Button disabled={page >= totalPages} onClick={() => setPage((old) => Math.min(totalPages, old + 1))}>
                    ›
                  </Button>
                </Box>
              </>
            )}
          </div>
        )}

        <Dialog
          open={ccirDialog}
          onClose={() => setCcirDialog(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { bgcolor: "var(--ig-f-2c2c2c)", color: "var(--ig-t-ffffff)" } }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            Selecionar CCIR Base - Ano {form.ano}
            <IconButton onClick={() => setCcirDialog(false)}>
              <CloseIcon sx={{ color: "var(--ig-t-ffffff)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <TextField
              fullWidth

              value={ccirSearch}

              onChange={(event) => {
                setCcirSearch(event.target.value);
                setCcirPage(0);
              }}

              placeholder="Código CCIR, imóvel rural, CAR, matrícula, apelido, município ou UF..."

              sx={{ ...muiFieldSx, mb: 2 }}
            />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CCIR</TableCell>

                  <TableCell>Imóvel Rural</TableCell>

                  <TableCell>CAR</TableCell>

                  <TableCell>Apelido</TableCell>

                  <TableCell>Matrícula</TableCell>

                  <TableCell>Município/UF</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedCcirs.map((item) => (
                  <TableRow key={item.ccirId} hover onClick={() => selectCcir(item)} sx={{ cursor: "pointer" }}>
                    <TableCell>{item.codigoCcir}</TableCell>

                    <TableCell>{item.nomeImovelRural}</TableCell>

                    <TableCell>{item.codigoCar}</TableCell>

                    <TableCell>{item.apelido}</TableCell>

                    <TableCell>{item.codigoMatricula}</TableCell>

                    <TableCell>{[item.municipioSedeImovelRural, item.uf].filter(Boolean).join("/")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"

              count={visibleCcirs.length}

              page={ccirPage}

              onPageChange={(_, value) => setCcirPage(value)}

              rowsPerPage={ccirRows}

              onRowsPerPageChange={(event) => {
                setCcirRows(Number(event.target.value));
                setCcirPage(0);
              }}

              rowsPerPageOptions={[10, 25, 50]}

              labelRowsPerPage="Por página:"

              sx={{ color: "var(--ig-t-ffffff)" }}
            />
          </DialogContent>
        </Dialog>

        {gedDialog && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - CCIR ANUAL #{gedRecordId}</h3>

              {files.length === 0 ? (
                <p>Nenhum arquivo cadastrado.</p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {files.map((file) => (
                    <li key={file.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ flex: 1 }}>{file.nome}</span>

                      <button type="button" className="btn-visualizar-ged" onClick={() => viewFile(gedRecordId, file)}>
                        <FaEye /> Visualizar
                      </button>

                      <button type="button" className="btn-baixar-ged" onClick={() => downloadFile(gedRecordId, file)}>
                        Baixar
                      </button>

                      {canDeleteFile && (
                        <button type="button" className="btn-excluir-ged" onClick={() => deleteFile(gedRecordId, file.id)}>
                          <FaTrash /> Excluir
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setGedDialog(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {canWrite ? (
          <div className="bloco-formulario">
            <form className="form-grid" onSubmit={submit}>
              {sectionTitle("DADOS DO IMÓVEL RURAL")}

              <div className="form-row">
                <TextField
                  label="Ano do CCIR*"
                  type="number"
                  value={form.ano}
                  disabled={locked}
                  error={fieldError("ano")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("ano", digits(e.target.value).slice(0, 4))}
                />

                <TextField
                  label="Buscar Código CCIR*"

                  value={form.codigoCcir}

                  disabled={locked}

                  error={fieldError("ccirId")}

                  sx={muiFieldSx}

                  fullWidth

                  InputProps={{ readOnly: true, endAdornment: <SearchIcon /> }}

                  onClick={() => {
                    if (locked) return;

                    if (!form.ano) return alert("Informe primeiro o Ano do CCIR.");

                    loadCcirs(form.ano);

                    setCcirDialog(true);
                  }}
                />

                <TextField label="Código Anual" value={form.codigoAnual} disabled sx={muiFieldSx} fullWidth />

                <TextField label="Apelido" value={form.apelido} disabled sx={muiFieldSx} fullWidth />
              </div>

              <div className="form-row">
                <TextField label="Nome do Imóvel Rural" value={form.nomeImovelRural} disabled sx={muiFieldSx} fullWidth />

                <TextField label="Código do Imóvel Rural" value={form.codigoImovelRural} disabled sx={muiFieldSx} fullWidth />

                <TextField label="CAR" value={form.codigoCar} disabled sx={muiFieldSx} fullWidth />

                <TextField label="Matrícula" value={form.codigoMatricula} disabled sx={muiFieldSx} fullWidth />
              </div>

              <div className="form-row">
                <TextField label="Município" value={form.municipioSedeImovelRural} disabled sx={muiFieldSx} fullWidth />

                <TextField label="UF" value={form.uf} disabled sx={muiFieldSx} fullWidth />

                <TextField label="Área Total (ha)" value={form.areaTotalHa} disabled sx={muiFieldSx} fullWidth />
              </div>

              <div className="form-row">
                {[
                  ["moduloRuralHa", "Módulo Rural (ha)*"],

                  ["numeroModulosRurais", "Nº de Módulos Rurais*"],

                  ["moduloFiscalHa", "Módulo Fiscal (ha)*"],

                  ["numeroModulosFiscais", "Nº de Módulos Fiscais*"],
                ].map(([field, label]) => (
                  <TextField
                    key={field}
                    label={label}
                    value={form[field]}
                    disabled={locked}
                    error={fieldError(field)}
                    sx={muiFieldSx}
                    fullWidth
                    onChange={(e) => updateForm(field, decimalTyping(e.target.value, 4))}
                    onBlur={() => updateForm(field, toBrDecimal(form[field], 4))}
                  />
                ))}
              </div>

              <div className="form-row">
                <TextField
                  label="Fração Mínima de Parcelamento (ha)*"
                  value={form.fracaoMinimaParcelamentoHa}
                  disabled={locked}
                  error={fieldError("fracaoMinimaParcelamentoHa")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("fracaoMinimaParcelamentoHa", decimalTyping(e.target.value, 2))}
                  onBlur={() => updateForm("fracaoMinimaParcelamentoHa", toBrDecimal(form.fracaoMinimaParcelamentoHa, 2))}
                />
              </div>

              {sectionTitle("SITUAÇÃO JURÍDICA DO IMÓVEL RURAL (ÁREAS REGISTRADAS)")}

              <div className="form-row">
                <TextField
                  label="UF/Município do Cartório*"
                  value={form.ufMunicipioCartorio}
                  disabled={locked}
                  error={fieldError("ufMunicipioCartorio")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("ufMunicipioCartorio", e.target.value.toUpperCase())}
                />

                <TextField
                  label="Data do Registro*"
                  type="date"
                  value={form.dataRegistro}
                  disabled={locked}
                  error={fieldError("dataRegistro")}
                  sx={muiFieldSx}
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  onChange={(e) => updateForm("dataRegistro", e.target.value)}
                />

                <TextField
                  label="CNS/Ofício*"
                  value={form.cnsOuOficio}
                  disabled={locked}
                  error={fieldError("cnsOuOficio")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("cnsOuOficio", e.target.value.toUpperCase())}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Matrícula/Transcrição*"
                  value={form.matriculaTranscricao}
                  disabled={locked}
                  error={fieldError("matriculaTranscricao")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("matriculaTranscricao", e.target.value.toUpperCase())}
                />

                <TextField
                  label="Registro*"
                  value={form.registro}
                  disabled={locked}
                  error={fieldError("registro")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("registro", e.target.value.toUpperCase())}
                />

                <TextField
                  label="Livro/Ficha*"
                  value={form.livroFicha}
                  disabled={locked}
                  error={fieldError("livroFicha")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("livroFicha", e.target.value.toUpperCase())}
                />
              </div>

              {sectionTitle("ÁREA DO IMÓVEL RURAL (ha)")}

              <div className="form-row">
                {[
                  ["areaRegistrada", "Área Registrada (ha)*"],

                  ["posseJustoTitulo", "Posse a Justo Título (ha)*"],

                  ["posseSimplesOcupacao", "Posse por Simples Ocupação (ha)*"],

                  ["areaMedida", "Área Medida (ha)*"],
                ].map(([field, label]) => (
                  <TextField
                    key={field}
                    label={label}
                    value={form[field]}
                    disabled={locked}
                    error={fieldError(field)}
                    sx={muiFieldSx}
                    fullWidth
                    onChange={(e) => updateForm(field, decimalTyping(e.target.value, 4))}
                    onBlur={() => updateForm(field, toBrDecimal(form[field], 4))}
                  />
                ))}
              </div>

              {sectionTitle("DADOS DO DECLARANTE")}

              <div className="form-row">
                <TextField
                  label="CPF ou CNPJ do Declarante*"
                  value={form.declaranteDocumento}
                  disabled={locked || consultandoDeclarante}
                  error={fieldError("declaranteDocumento")}
                  sx={muiFieldSx}
                  fullWidth
                  inputProps={{
                    maxLength: 18,
                  }}
                  helperText={consultandoDeclarante ? "Consultando dados do CNPJ..." : "O tipo de pessoa e a razão social são preenchidos automaticamente."}
                  onChange={(e) => {
                    if (locked) return;

                    handleDeclaranteDocumentoChange(e.target.value);
                  }}
                />

                <TextField
                  label="Nome do Declarante*"
                  value={form.declaranteNome}
                  disabled={locked || consultandoDeclarante || form.declaranteTipoPessoa === "PJ"}
                  error={fieldError("declaranteNome")}
                  sx={muiFieldSx}
                  fullWidth
                  helperText={form.declaranteTipoPessoa === "PJ" ? "Razão social preenchida pela consulta do CNPJ." : ""}
                  onChange={(e) => {
                    if (locked) return;

                    updateForm("declaranteNome", e.target.value.toUpperCase());
                  }}
                />
                <TextField
                  label="Tipo de Pessoa*"
                  value={form.declaranteTipoPessoa}
                  disabled
                  sx={muiFieldSx}
                  error={fieldError("declaranteTipoPessoa")}
                  fullWidth
                />

                <TextField
                  label="Total de Pessoas Relacionadas*"
                  type="number"
                  value={form.totalPessoasRelacionadas}
                  disabled={locked}
                  error={fieldError("totalPessoasRelacionadas")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("totalPessoasRelacionadas", digits(e.target.value))}
                />
              </div>

              {sectionTitle("DADOS DOSTITULARES")}

              {form.titulares.map((holder, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "100%",
                    p: 2,
                    mb: 1.5,
                    border: "1px solid var(--ig-b-334155)",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <strong>Titular {index + 1}</strong>

                    {!locked && index === 0 && (
                      <Button
                        type="button"
                        variant="contained"
                        onClick={addHolder}
                        sx={{
                          minWidth: 180,
                          height: 40,
                          backgroundColor: "#2196f3",
                          color: "#ffffff",
                          fontWeight: 700,
                          textTransform: "none",

                          "&:hover": {
                            backgroundColor: "#1976d2",
                          },
                        }}
                      >
                        Adicionar Titular
                      </Button>
                    )}

                    {!locked && index > 0 && (
                      <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        startIcon={<FaTrash />}
                        onClick={() => removeHolder(index)}
                        sx={{
                          minWidth: 110,
                          height: 40,
                          fontWeight: 700,
                          textTransform: "none",
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>

                  <div className="form-row">
                    <TextField
                      label="CPF/CNPJ*"
                      value={holder.documento}
                      disabled={locked || titularConsultando === index}
                      error={holderError(holder, "documento")}
                      sx={muiFieldSx}
                      fullWidth
                      inputProps={{
                        maxLength: 18,
                      }}
                      helperText={titularConsultando === index ? "Consultando dados do CNPJ..." : "Tipo de pessoa e razão social automáticos para CNPJ."}
                      onChange={(e) => {
                        if (locked) return;

                        handleTitularDocumentoChange(index, e.target.value);
                      }}
                    />

                    <TextField
                      label="Nome*"
                      value={holder.nome}
                      disabled={locked || titularConsultando === index || holder.tipoPessoa === "PJ"}
                      error={holderError(holder, "nome")}
                      sx={muiFieldSx}
                      fullWidth
                      helperText={holder.tipoPessoa === "PJ" ? "Razão social preenchida pela consulta do CNPJ." : ""}
                      onChange={(e) => {
                        if (locked) return;

                        updateHolder(index, "nome", e.target.value.toUpperCase());
                      }}
                    />

                    <TextField label="Tipo de Pessoa*" value={holder.tipoPessoa} disabled sx={muiFieldSx} error={holderError(holder, "tipoPessoa")} fullWidth />

                    <TextField
                      label="Condição*"
                      value={holder.condicao}
                      disabled={locked}
                      error={holderError(holder, "condicao")}
                      sx={muiFieldSx}
                      fullWidth
                      onChange={(e) => updateHolder(index, "condicao", e.target.value.toUpperCase())}
                    />

                    <TextField
                      label="Detenção (%)*"
                      value={holder.detencao}
                      disabled={locked}
                      error={holderError(holder, "detencao")}
                      sx={muiFieldSx}
                      fullWidth
                      onChange={(e) => updateHolder(index, "detencao", decimalTyping(e.target.value, 2))}
                      onBlur={() => updateHolder(index, "detencao", toBrDecimal(holder.detencao, 2))}
                    />
                  </div>
                </Box>
              ))}

              {sectionTitle("DADOS DE CONTROLE")}

              <div className="form-row">
                <TextField
                  label="Data de Lançamento*"
                  type="date"
                  value={form.dataLancamento}
                  disabled={locked}
                  error={fieldError("dataLancamento")}
                  sx={muiFieldSx}
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  onChange={(e) => updateForm("dataLancamento", e.target.value)}
                />

                <TextField
                  label="Número do CCIR*"
                  value={form.numeroCcirDocumento}
                  disabled={locked}
                  error={fieldError("numeroCcirDocumento")}
                  sx={muiFieldSx}
                  fullWidth
                  onChange={(e) => updateForm("numeroCcirDocumento", e.target.value.toUpperCase())}
                />

                <TextField
                  label="Data de Geração do CCIR*"
                  type="date"
                  value={form.dataGeracaoCcir}
                  disabled={locked}
                  error={fieldError("dataGeracaoCcir")}
                  sx={muiFieldSx}
                  fullWidth
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  onChange={(e) => updateForm("dataGeracaoCcir", e.target.value)}
                />
              </div>

              {sectionTitle("TAXAS DE SERVIÇOS CADASTRAIS (R$)")}

              <div className="form-row">
                {[
                  ["debitosAnteriores", "Débitos Anteriores*"],

                  ["taxasServicosCadastrais", "Taxas de Serviços Cadastrais*"],

                  ["valorCobrado", "Valor Cobrado*"],
                ].map(([field, label]) => (
                  <TextField
                    key={field}
                    label={label}
                    value={form[field]}
                    disabled={locked}
                    error={fieldError(field)}
                    sx={muiFieldSx}
                    fullWidth
                    onChange={(e) => updateForm(field, moneyTyping(e.target.value))}
                  />
                ))}
              </div>

              {sectionTitle("OBSERVAÇÃO")}

              <div className="form-row">
                <TextField
                  label="Observação"
                  value={form.observacao}
                  disabled={locked}
                  sx={muiFieldSx}
                  fullWidth
                  multiline
                  minRows={4}
                  onChange={(event) => updateForm("observacao", event.target.value.toUpperCase())}
                />
              </div>

              <div className="form-botoes" style={{ marginTop: 20 }}>
                {viewMode && !editingId && canWrite && (
                  <button type="button" className="btn-cadastrar" onClick={startEditing}>
                    <FaEdit /> Editar
                  </button>
                )}

                {(!viewMode || editingId) && (
                  <button type="submit" className="btn-cadastrar" style={{ flex: 1 }}>
                    <FaSave /> {editingId ? "Atualizar" : "Cadastrar"}
                  </button>
                )}

                <button type="button" className="btn-acao" style={{ flex: 1 }} onClick={() => clearForm(selectedYear)}>
                  <FaTimes /> Cancelar
                </button>

                {viewMode && !editingId && canDeleteRecord && (
                  <button type="button" className="btn-excluir" onClick={deleteRecord}>
                    <FaTrash /> Excluir
                  </button>
                )}

                <button
                  type="button"

                  className={`btn-ged btn-ged-form ${hasGed ? "btn-ged-ok" : ""}`}

                  style={{ flex: 1 }}

                  onClick={() => document.getElementById("uploadGEDCCIRAnual")?.click()}
                >
                  <FaPaperclip /> GED
                  {pendingFiles.length > 0 && !editingId && !viewingId && <span className="ged-badge">{pendingFiles.length}</span>}
                </button>
              </div>

              {!locked && pendingFiles.length > 0 && (
                <div style={{ width: "100%", marginTop: 12 }}>
                  <label>Arquivos pendentes para anexar</label>

                  {pendingFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} style={{ display: "flex", gap: 16, marginTop: 10 }}>
                      <input value={file.name} readOnly className="campo-formulario-padrao" style={{ flex: 1 }} />

                      <button type="button" className="btn-excluir" onClick={() => setPendingFiles((old) => old.filter((_, fileIndex) => fileIndex !== index))}>
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(editingId || viewingId) && files.length > 0 && (
                <div style={{ width: "100%", marginTop: 12 }}>
                  <label>Arquivos anexados neste CCIR anual</label>

                  {files.map((file) => (
                    <div key={file.id} style={{ display: "flex", gap: 10, marginTop: 10 }}>
                      <input value={file.nome} readOnly className="campo-formulario-padrao" style={{ flex: 1 }} />

                      <button type="button" className="btn-visualizar-ged" onClick={() => viewFile(editingId || viewingId, file)}>
                        <FaEye /> Visualizar
                      </button>

                      <button type="button" className="btn-baixar-ged" onClick={() => downloadFile(editingId || viewingId, file)}>
                        Baixar
                      </button>

                      {canDeleteFile && (
                        <button type="button" className="btn-excluir-ged" onClick={() => deleteFile(editingId || viewingId, file.id)}>
                          <FaTrash /> Excluir
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <input
                id="uploadGEDCCIRAnual"

                type="file"

                multiple

                style={{ display: "none" }}

                onChange={async (event) => {
                  const selected = Array.from(event.target.files || []);

                  event.target.value = "";

                  if (!selected.length) return;

                  const currentId = editingId || viewingId;

                  if (!currentId) {
                    setPendingFiles((old) => [...old, ...selected]);

                    alert(`Arquivos preparados (${selected.length}). Clique em Cadastrar para anexar.`);

                    return;
                  }

                  const ok = await uploadFiles(currentId, selected);

                  if (ok) {
                    alert("Arquivos enviados com sucesso!");

                    await loadFiles(currentId, false);

                    if (selectedYear) await loadYear(selectedYear, true);
                  } else {
                    alert("Falha ao enviar arquivos.");
                  }
                }}
              />
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}
